import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../lib/auth';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type Status =
  | 'OPEN'
  | 'ROOT_CAUSE_ANALYSIS'
  | 'ACTION_PLAN'
  | 'IMPLEMENTATION'
  | 'VERIFICATION'
  | 'CLOSED'
  | 'CANCELLED';

interface Capa {
  _id: string;
  capaNumber: string;
  title: string;
  description: string;
  triggerType: 'NCR' | 'AUDIT' | 'CUSTOMER_COMPLAINT' | 'INTERNAL' | 'MANAGEMENT_REVIEW';
  triggerRef: string | null;
  severity: Severity;
  status: Status;
  ownerEmail: string | null;
  dueDate: string | null;
  createdAt: string;
  closedAt: string | null;
}

const STATUS_LABEL: Record<Status, { label: string; class: string }> = {
  OPEN: { label: 'Açık', class: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
  ROOT_CAUSE_ANALYSIS: {
    label: 'KNA',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
  ACTION_PLAN: {
    label: 'Aksiyon Planı',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
  IMPLEMENTATION: {
    label: 'Uygulama',
    class: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  },
  VERIFICATION: {
    label: 'Doğrulama',
    class: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  },
  CLOSED: {
    label: 'Kapalı',
    class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
  CANCELLED: {
    label: 'İptal',
    class: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  },
};

const SEVERITY_LABEL: Record<Severity, { label: string; class: string }> = {
  LOW: {
    label: 'Düşük',
    class: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  },
  MEDIUM: {
    label: 'Orta',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
  HIGH: {
    label: 'Yüksek',
    class: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  },
  CRITICAL: {
    label: 'Kritik',
    class: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  },
};

export function CapaPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const canWrite = hasPermission('capa.write');

  const { data, isLoading, isError, error } = useQuery<{
    items: Capa[];
    total: number;
  }>({
    queryKey: ['capa', 'list'],
    queryFn: () => api.get('/v1/capa'),
  });

  const create = useMutation({
    mutationFn: (payload: unknown) => api.post<Capa>('/v1/capa', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capa', 'list'] });
      setShowForm(false);
    },
  });

  return (
    <div>
      <PageHeader
        title="Düzeltici Faaliyet (CAPA)"
        subtitle="SE-PR-05 — Tetikleyici: NCR, iç tetkik, müşteri şikayeti, kritik olay"
        clause="§10.2"
        actions={
          canWrite ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? 'Kapat' : 'Yeni CAPA'}
            </button>
          ) : undefined
        }
      />

      {showForm && canWrite && (
        <div className="card mb-4 p-5">
          <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
            Yeni CAPA Taslağı
          </h2>
          <CapaForm
            busy={create.isPending}
            error={create.error instanceof Error ? create.error.message : null}
            onSubmit={(payload) => create.mutate(payload)}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {isLoading && (
        <div className="card p-6 text-sm text-slate-500 dark:text-slate-400">Yükleniyor…</div>
      )}
      {isError && (
        <div className="card p-6 text-sm text-rose-700 dark:text-rose-300">
          {error instanceof Error ? error.message : 'Hata'}
        </div>
      )}

      {data && (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left">No</th>
                <th className="px-4 py-3 text-left">Başlık</th>
                <th className="px-4 py-3 text-left">Tetikleyici</th>
                <th className="px-4 py-3 text-left">Şiddet</th>
                <th className="px-4 py-3 text-left">Durum</th>
                <th className="px-4 py-3 text-left">Sorumlu</th>
                <th className="px-4 py-3 text-left">Termin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.items.map((c) => (
                <tr key={c._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-slate-700 dark:text-slate-300">
                    {c.capaNumber}
                  </td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-800 dark:text-slate-200">{c.title}</div>
                    <div className="line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
                      {c.description}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-700 dark:text-slate-300">
                    {c.triggerType}
                    {c.triggerRef && (
                      <div className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
                        {c.triggerRef}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`badge ${SEVERITY_LABEL[c.severity].class}`}>
                      {SEVERITY_LABEL[c.severity].label}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`badge ${STATUS_LABEL[c.status].class}`}>
                      {STATUS_LABEL[c.status].label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                    {c.ownerEmail ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                    {c.dueDate ? new Date(c.dueDate).toLocaleDateString('tr-TR') : '—'}
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    Henüz CAPA kaydı yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
            {data.items.length} kayıt
          </div>
        </div>
      )}
    </div>
  );
}

function CapaForm({
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  onSubmit: (payload: unknown) => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<Capa['triggerType']>('NCR');
  const [triggerRef, setTriggerRef] = useState('');
  const [severity, setSeverity] = useState<Severity>('MEDIUM');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [dueDate, setDueDate] = useState('');

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      title,
      description,
      triggerType,
      triggerRef: triggerRef || null,
      severity,
      ownerEmail: ownerEmail || null,
      dueDate: dueDate ? new Date(dueDate + 'T00:00:00Z').toISOString() : null,
    });
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <label className="label" htmlFor="capa-title">Başlık</label>
        <input
          id="capa-title"
          className="input"
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="sm:col-span-2">
        <label className="label" htmlFor="capa-desc">Açıklama (problem + tetikleyici bağlam)</label>
        <textarea
          id="capa-desc"
          className="input min-h-[88px]"
          required
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="capa-trigger-type">Tetikleyici Tipi</label>
        <select
          id="capa-trigger-type"
          className="input"
          value={triggerType}
          onChange={(e) => setTriggerType(e.target.value as Capa['triggerType'])}
        >
          <option value="NCR">NCR</option>
          <option value="AUDIT">İç Tetkik</option>
          <option value="CUSTOMER_COMPLAINT">Müşteri Şikayeti</option>
          <option value="INTERNAL">Dahili</option>
          <option value="MANAGEMENT_REVIEW">YGG</option>
        </select>
      </div>
      <div>
        <label className="label" htmlFor="capa-trigger-ref">Tetikleyici Referans (ör. NCR no)</label>
        <input
          id="capa-trigger-ref"
          className="input"
          placeholder="opsiyonel"
          value={triggerRef}
          onChange={(e) => setTriggerRef(e.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="capa-severity">Şiddet</label>
        <select
          id="capa-severity"
          className="input"
          value={severity}
          onChange={(e) => setSeverity(e.target.value as Severity)}
        >
          <option value="LOW">Düşük</option>
          <option value="MEDIUM">Orta</option>
          <option value="HIGH">Yüksek</option>
          <option value="CRITICAL">Kritik</option>
        </select>
      </div>
      <div>
        <label className="label" htmlFor="capa-owner">Sorumlu (e-posta)</label>
        <input
          id="capa-owner"
          type="email"
          className="input"
          value={ownerEmail}
          onChange={(e) => setOwnerEmail(e.target.value)}
        />
      </div>
      <div className="sm:col-span-2">
        <label className="label" htmlFor="capa-due">Termin</label>
        <input
          id="capa-due"
          type="date"
          className="input"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </div>
      {error && (
        <div className="sm:col-span-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
          {error}
        </div>
      )}
      <div className="sm:col-span-2 flex items-center gap-2">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Kaydediliyor…' : 'Taslağı Kaydet'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          İptal
        </button>
      </div>
    </form>
  );
}
